import type {
  AssignmentLine,
  CartAssignment,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import {
  getDivergentShipToId,
  getDivergentShipToName,
} from '../components/divergentShipTo';
import type { FilterableColumn } from './columnsHiddenByFilters';

type ColumnLine = Pick<
  AssignmentLine,
  | 'destinationWarehouseId'
  | 'destinationWarehouseName'
  | 'buyerCode'
  | 'popularityCode'
  | 'stockStatus'
  | 'shipToWarehouseId'
  | 'topOption'
>;

export interface ActiveLineFilters {
  /** Destination warehouses picked in the page filters. */
  warehouseIds?: readonly string[];
  buyerCodes?: readonly string[];
  popularityCodes?: readonly string[];
  stockStatuses?: readonly string[];
  /** The toolbar's ship-to, warehouse or hub focus has a pick. */
  locationFocused: boolean;
}

const hasPick = (values: readonly string[] | undefined) =>
  values !== undefined && values.length > 0;

const codeColumn = (
  id: string,
  filtered: boolean,
  code: (line: ColumnLine) => string | null,
): FilterableColumn<ColumnLine> => ({
  id,
  filtered,
  key: (l) => code(l) ?? '',
  label: (l) => code(l) ?? '',
});

/** The line table's columns a board filter can narrow to one value. */
export function lineFilterColumns(
  filters: ActiveLineFilters,
  cartsByShipTo?: Map<string, CartAssignment>,
): FilterableColumn<ColumnLine>[] {
  return [
    {
      id: 'warehouse',
      filtered: hasPick(filters.warehouseIds) || filters.locationFocused,
      // The cell also names a divergent PO ship-to under the destination.
      key: (l) =>
        `${l.destinationWarehouseId}|${getDivergentShipToId(l) ?? ''}`,
      label: (l) => {
        const shipTo = getDivergentShipToName(l, cartsByShipTo);
        return shipTo === null
          ? l.destinationWarehouseName
          : `${shipTo} → ${l.destinationWarehouseName}`;
      },
    },
    codeColumn('buyer', hasPick(filters.buyerCodes), (l) => l.buyerCode),
    codeColumn(
      'pop',
      hasPick(filters.popularityCodes),
      (l) => l.popularityCode,
    ),
    codeColumn('stock', hasPick(filters.stockStatuses), (l) => l.stockStatus),
  ];
}
